import { useSize } from 'ahooks';
import { differenceInDays, format, parse } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { shiftDateDataIndexFormat } from '../../lib/utils/date-time';
import { Optional } from '../Shared';
import ShiftButton from './ShiftButton';
import ShiftControl from './ShiftControl';
import TeamRoleCell from './TeamRoleCell';
import { isShiftCell, roleColumnDataIndex } from './lib/util';
import type {
  RoleInformation,
  RosterDisplayMode,
  RosterTableRowRecord,
  RosterUserRecord,
  Shift,
  ShiftBackgroundColours,
  ShiftPeriod,
} from './types';

type RosterCellProps = {
  readonly rosterDisplayMode: RosterDisplayMode;
  readonly rosterTableRowRecord: RosterTableRowRecord & RosterUserRecord;
  readonly dataIndex: string;
  readonly readOnly: boolean;
  readonly allowAssignUser: boolean;
  readonly dayShift: ShiftPeriod;
  readonly shiftBackgroundColours: ShiftBackgroundColours;
  readonly roleInformation: readonly RoleInformation[];
  /**
   * Buffer date that is one day before the roster date range's first date.
   * Used to render any overflow shifts from previous date range.
   */
  readonly bufferFirstRosterDate: Date;
  readonly showShiftStatus: boolean;
};

type ShiftCellProps = {
  readonly rosterTableRowRecord: RosterTableRowRecord & RosterUserRecord;
  readonly dataIndex: string;
  readonly selectedDate: Date;
  readonly rosterDisplayMode: RosterDisplayMode;
  readonly readOnly: boolean;
  readonly dayShift: ShiftPeriod;
  readonly shiftBackgroundColours: ShiftBackgroundColours;
  readonly cellWidth?: number;
  /**
   * Buffer date that is one day before the roster date range's first date.
   * Used to render any overflow shifts from previous date range.
   */
  readonly bufferFirstRosterDate: Date;
  readonly showShiftStatus: boolean;
};

/**
 * Gets the overflow shifts from the provided possible overflow shifts.
 * The overflow shifts are the shifts from previous roster date range and have the duration span to the next date range.
 * @param bufferFirstRosterDataIndex the buffered first roster data index which the data index (date) from previous date range's last cell.
 * @param dataIndex current data index (date format).
 * @param maybeOverflowShifts retrieved maybe overflow shifts.
 * @returns overflow shifts with overflow status.
 */
const getOverflowShifts = (
  bufferFirstRosterDataIndex: string,
  dataIndex: string,
  maybeOverflowShifts?: readonly Shift[]
) => {
  // This current date column (based on data index) should check for overflow shifts if the current date's previous date
  // is the configured buffer first roster date. Usually that means the current data index corresponds with first date column on the roster.
  const checkOverflowShifts =
    differenceInDays(
      parse(dataIndex, shiftDateDataIndexFormat, new Date()),
      parse(bufferFirstRosterDataIndex, shiftDateDataIndexFormat, new Date())
    ) === 1;

  if (checkOverflowShifts && maybeOverflowShifts !== undefined) {
    // Shift is overflowed if the end date of the shift from previous date range falls into the current date range's dataIndex.
    return maybeOverflowShifts
      .filter(
        (shift) =>
          format(new Date(shift.endTime), shiftDateDataIndexFormat) ===
          dataIndex
      )
      .map((shift) => ({
        ...shift,
        overflow: true,
      }));
  }

  return [];
};

/**
 * Renders a table cell with buttons for the roster Shifts.
 */
const ShiftCell = ({
  rosterTableRowRecord,
  dataIndex,
  selectedDate,
  rosterDisplayMode,
  cellWidth,
  dayShift,
  shiftBackgroundColours,
  bufferFirstRosterDate,
  readOnly,
  showShiftStatus,
}: ShiftCellProps) => {
  const bufferFirstRosterDataIndex = format(
    bufferFirstRosterDate,
    shiftDateDataIndexFormat
  );

  const vacantShifts = rosterTableRowRecord.vacantShifts?.[dataIndex] ?? [];

  const vacantOverflowShifts: readonly Shift[] = getOverflowShifts(
    bufferFirstRosterDataIndex,
    dataIndex,
    rosterTableRowRecord.vacantShifts?.[bufferFirstRosterDataIndex]
  );

  return (
    <>
      {[...vacantShifts, ...vacantOverflowShifts].map((shift) => {
        const vacantShiftCellRecord = {
          selectedDate,
          teamRoleAssignmentId: rosterTableRowRecord.teamRoleAssignmentId,
          roleName: rosterTableRowRecord.roleName,
        };

        return (
          <ShiftButton
            key={`${shift.id}-vacant`}
            cellWidth={cellWidth}
            rosterDisplayMode={rosterDisplayMode}
            cellRecord={vacantShiftCellRecord}
            shift={shift}
            readOnly={readOnly}
            dayShift={dayShift}
            shiftBackgroundColours={shiftBackgroundColours}
            showShiftStatus={showShiftStatus}
          />
        );
      })}
      {rosterTableRowRecord.users?.map((user) => {
        const maybeShifts = user[dataIndex];

        const maybeOverflowShifts = user[bufferFirstRosterDataIndex];

        if (isShiftCell(maybeShifts) && isShiftCell(maybeOverflowShifts)) {
          const userCellRecord = {
            selectedDate,
            userId: user.userId,
            userRoleAssignmentId: user.userRoleAssignmentId,
            firstName: user.firstName,
            lastName: user.lastName,
            teamRoleAssignmentId: rosterTableRowRecord.teamRoleAssignmentId,
            roleName: rosterTableRowRecord.roleName,
          };

          const overflowShifts: readonly Shift[] = getOverflowShifts(
            bufferFirstRosterDataIndex,
            dataIndex,
            maybeOverflowShifts
          );

          return [...(maybeShifts ?? []), ...overflowShifts].map((shift) => (
            <ShiftButton
              key={`${shift.id}-filled`}
              cellWidth={cellWidth}
              rosterDisplayMode={rosterDisplayMode}
              cellRecord={userCellRecord}
              shift={{ ...shift, user: { ...user, userName: user.user_name } }}
              readOnly={readOnly}
              dayShift={dayShift}
              shiftBackgroundColours={shiftBackgroundColours}
              showShiftStatus={showShiftStatus}
            />
          ));
        }

        return <></>;
      })}
    </>
  );
};

/**
 * Renders a table cell that displays a piece of roster information depending on the supplied data index and current row record.
 * The cell value is extracted from the row record with data index.
 * E.g. record[roleColumnDataIndex] renders role name or user name. record['20220725'] renders the Shift data with custom rendering logic.
 */
const RosterCell = ({
  rosterTableRowRecord,
  dataIndex,
  rosterDisplayMode,
  readOnly,
  allowAssignUser,
  dayShift,
  shiftBackgroundColours,
  roleInformation,
  bufferFirstRosterDate,
  showShiftStatus,
}: RosterCellProps) => {
  const cellDivRef = useRef<HTMLDivElement>(null);

  const [cellDivWidthInPixel, setCellDivWidth] = useState<number>(0);

  const windowSize = useSize(document.querySelector('body'));

  // Calculates the current cell's <div> DOM width, used to determine the Shift button width based on length of the Shift.
  useEffect(() => {
    const currentCellDivWidth = cellDivRef.current?.clientWidth;

    if (
      currentCellDivWidth !== undefined &&
      (windowSize?.width ?? 0) > 0 &&
      cellDivWidthInPixel !== currentCellDivWidth
    ) {
      setCellDivWidth(currentCellDivWidth);
    }
  }, [cellDivWidthInPixel, windowSize]);

  if (dataIndex === roleColumnDataIndex) {
    const assignedUsers: readonly RosterUserRecord[] =
      rosterTableRowRecord.users
        ?.filter((user) => !user.historic)
        .toSorted((user1, user2) =>
          user1.firstName.toLowerCase() > user2.firstName.toLowerCase() ? 1 : -1
        ) ?? [];

    const roleInfo = roleInformation.find(
      (roleInfo) => roleInfo.name === rosterTableRowRecord.roleName
    );

    const teamRoleAssignmentId = rosterTableRowRecord.teamRoleAssignmentId;

    return (
      <TeamRoleCell
        title={rosterTableRowRecord[dataIndex]}
        assignedUsers={assignedUsers}
        readOnly={readOnly}
        roleInfo={roleInfo}
        teamRoleAssignmentId={teamRoleAssignmentId}
      />
    );
  }

  const selectedDate = parse(dataIndex, shiftDateDataIndexFormat, new Date());

  const addShiftCellRecord = {
    selectedDate,
    teamRoleAssignmentId: rosterTableRowRecord.teamRoleAssignmentId,
    roleName: rosterTableRowRecord.roleName,
  };

  return (
    <>
      <div
        ref={cellDivRef}
        style={{
          width: '100%',
          display: 'inline-block',
          position: 'relative',
        }}
      >
        <ShiftCell
          bufferFirstRosterDate={bufferFirstRosterDate}
          rosterTableRowRecord={rosterTableRowRecord}
          cellWidth={cellDivWidthInPixel}
          dayShift={dayShift}
          dataIndex={dataIndex}
          shiftBackgroundColours={shiftBackgroundColours}
          selectedDate={selectedDate}
          rosterDisplayMode={rosterDisplayMode}
          readOnly={readOnly}
          showShiftStatus={showShiftStatus}
        />
      </div>

      <Optional show={allowAssignUser && !readOnly}>
        <ShiftControl dayShift={dayShift} cellRecord={addShiftCellRecord} />
      </Optional>
    </>
  );
};

export default RosterCell;
